import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowLeft, CheckCircle, XCircle, MessageSquare, Loader2, AlertCircle,
} from 'lucide-react';
import { proposalsApi } from '../utils/api';
import MspProposalSummary from '../components/MspProposalSummary';
import ConstructionProposalSummary from '../components/ConstructionProposalSummary';
import ScopedProjectSummary from '../components/ScopedProjectSummary';
import ComplianceProposalSummary from '../components/ComplianceProposalSummary';
import InsuranceProposalSummary from '../components/InsuranceProposalSummary';
import ProposalCommentThread from '../components/ProposalCommentThread';

// Status labelling — derives a single "state" from the stage + lifecycle timestamps.
function deriveStatus(stage, lifecycle) {
  if (stage === 'declined') {
    return { key: 'declined', label: 'Declined', color: '#dc2626', bg: '#fee2e2' };
  }
  if (['contract', 'onboarding', 'active', 'completed'].includes(stage)) {
    return { key: 'accepted', label: 'Accepted', color: '#16a34a', bg: '#dcfce7' };
  }
  if (lifecycle?.viewed_at) {
    return { key: 'viewed', label: 'Viewed', color: '#3b82f6', bg: '#dbeafe' };
  }
  if (lifecycle?.sent_at) {
    return { key: 'sent', label: 'Awaiting Your Review', color: '#ca8a04', bg: '#fef9c3' };
  }
  return { key: 'draft', label: stage || 'Draft', color: '#6b7280', bg: '#f3f4f6' };
}

export default function Proposal() {
  const { pathId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [showDecline, setShowDecline] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [acting, setActing] = useState(false);
  const [actionMessage, setActionMessage] = useState(null); // {type:'success'|'error', text}

  // Per-addon client choices. Currently the only optional add-on is
  // cloud_backup. Keys map to proposal section names; values are
  // true = accept, false = decline, undefined = not yet chosen.
  const [addonChoices, setAddonChoices] = useState({});

  const commentThreadRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const d = await proposalsApi.getProposal(pathId);
      setData(d);
      setError('');
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [pathId]);

  useEffect(() => { load(); }, [load]);

  // ?pm_added=1 — Stripe Checkout success_url landing. Confirm the payment
  // method actually attached, surface a success banner, and clean the URL.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('pm_added') !== '1') return;

    let cancelled = false;
    (async () => {
      try {
        const s = await proposalsApi.getOnboardingStatus(pathId);
        if (cancelled) return;
        if (s?.has_payment_method) {
          setActionMessage({ type: 'success', text: 'Payment method added.' });
        } else {
          setActionMessage({
            type: 'error',
            text: "We didn't see your new payment method yet. If you just added one, please refresh in a moment.",
          });
        }
      } catch (_e) {
        // Endpoint may not be deployed yet — show an optimistic banner.
        if (cancelled) return;
        setActionMessage({ type: 'success', text: 'Payment method added.' });
      } finally {
        if (!cancelled) {
          // Strip the query string so a refresh doesn't re-trigger this banner.
          navigate(location.pathname, { replace: true });
        }
      }
    })();

    return () => { cancelled = true; };
  }, [location.search, location.pathname, pathId, navigate]);

  // Optional add-ons offered on this proposal (sections with _is_optional).
  const optionalAddons = (() => {
    const p = data?.proposal || {};
    const result = [];
    if (p.cloud_backup && p.cloud_backup._is_optional) {
      const fee = Number(p.cloud_backup.monthly_fee) || 0;
      const d = Number(p.cloud_backup.discount) || 0;
      const discounted = p.cloud_backup.discount_type === 'percent'
        ? fee * (1 - d / 100) : Math.max(0, fee - d);
      result.push({
        key: 'cloud_backup',
        label: 'Boot to Cloud Backup',
        monthlyFee: fee,
        discountedFee: d > 0 ? discounted : fee,
        discountLabel: d > 0
          ? (p.cloud_backup.discount_type === 'percent' ? `${d}% off` : `$${d} off`)
          : null,
        description: p.cloud_backup.description || 'Cloud-replicated backup with rapid VM recovery',
      });
    }
    return result;
  })();

  const isConstruction = data?.path_type === 'construction';
  const isHardware = data?.path_type === 'hardware';
  const isInsurance = data?.path_type === 'insurance';
  const isCompliance = data?.path_type === 'compliance';
  // Compliance SRA/remediation are one-time (bill on accept); monitoring/tracking
  // are recurring and go through the contract flow.
  const complianceKind = data?.compliance?.kind
    || data?.proposal?._compliance_kind || 'sra';
  const isComplianceOneTime = isCompliance && ['sra', 'remediation'].includes(complianceKind);
  // One-time itemized jobs go straight to active + invoice on accept
  // (construction = 25% deposit; hardware/compliance = full amount). No Terms step.
  const isOneTime = isConstruction || isHardware || isComplianceOneTime;

  const handleAccept = async () => {
    if (isOneTime) {
      const confirmMsg = (isHardware || isComplianceOneTime)
        ? 'Accept this proposal? An invoice for the full amount will be emailed to you.'
        : 'Accept this proposal? A 25% deposit invoice will be emailed to you to start the project.';
      if (!window.confirm(confirmMsg)) return;
      setActing(true);
      setActionMessage(null);
      try {
        await proposalsApi.accept(pathId, {});
        setActionMessage({
          type: 'success',
          text: (isHardware || isComplianceOneTime)
            ? 'Proposal accepted — your invoice is on its way by email.'
            : 'Proposal accepted — your 25% deposit invoice is on its way by email.',
        });
        await load();
      } catch (e) {
        setActionMessage({ type: 'error', text: e.message });
      } finally {
        setActing(false);
      }
      return;
    }

    // Require client to explicitly choose for every offered add-on before
    // accepting — keeps "did they really want this?" disputes off the table.
    const undecided = optionalAddons.filter(a => addonChoices[a.key] === undefined);
    if (undecided.length > 0) {
      setActionMessage({
        type: 'error',
        text: `Please choose Add or Decline for: ${undecided.map(a => a.label).join(', ')}`,
      });
      return;
    }
    if (!window.confirm('Accept this proposal? You will be guided through Terms of Service signing and account setup.')) return;
    setActing(true);
    setActionMessage(null);
    try {
      const res = await proposalsApi.accept(pathId, addonChoices);
      // Forward to the contract-signing handoff page. Pass any signing
      // metadata along via location state so the next page doesn't have
      // to re-fetch it.
      navigate(`/contract/${pathId}`, {
        state: {
          signUrl: res?.sign_url || null,
          packageToken: res?.package_token || null,
        },
      });
    } catch (e) {
      setActionMessage({ type: 'error', text: e.message });
      setActing(false);
    }
  };

  const handleDeclineSubmit = async (e) => {
    e.preventDefault();
    const trimmed = declineReason.trim();
    if (!trimmed) return;
    setActing(true);
    setActionMessage(null);
    try {
      await proposalsApi.decline(pathId, trimmed);
      setActionMessage({ type: 'success', text: 'Proposal declined. Your reason has been shared with our team.' });
      setShowDecline(false);
      setDeclineReason('');
      await load();
    } catch (err) {
      setActionMessage({ type: 'error', text: err.message });
    } finally {
      setActing(false);
    }
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        <Loader2 className="spinner-large" size={48} />
        <p>Loading proposal…</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="proposal-page">
        <div className="proposal-topbar">
          <button onClick={() => navigate('/')} className="back-nav-button">
            <ArrowLeft size={16} /> Dashboard
          </button>
        </div>
        <div className="error-message">{error || 'Proposal not found.'}</div>
      </div>
    );
  }

  const status = deriveStatus(data.current_stage, data.lifecycle);
  const canAct = data.current_stage === 'proposal' || data.current_stage === 'declined';
  const isAccepted = ['contract', 'onboarding', 'active', 'completed'].includes(data.current_stage);
  const isDeclined = data.current_stage === 'declined';

  return (
    <div className="proposal-page">
      <div className="proposal-topbar">
        <button onClick={() => navigate('/')} className="back-nav-button">
          <ArrowLeft size={16} /> Dashboard
        </button>
      </div>

      <header className="proposal-header">
        <div>
          <h1>Proposal for {data.customer_name}</h1>
          {data.lifecycle?.sent_at && (
            <div className="proposal-header-meta">
              Sent {new Date(data.lifecycle.sent_at).toLocaleDateString()}
            </div>
          )}
        </div>
        <span className="proposal-status-pill" style={{ color: status.color, background: status.bg }}>
          {status.label}
        </span>
      </header>

      {isAccepted && isOneTime && (
        <div className="proposal-banner proposal-banner-success">
          <CheckCircle size={18} />
          <div style={{ flex: 1 }}>
            <strong>Proposal accepted.</strong>{' '}
            {(isHardware || isComplianceOneTime)
              ? 'Your invoice for the full amount has been emailed to you.'
              : 'Your 25% deposit invoice has been emailed to you — paying it starts the project. The balance is billed on completion.'}
          </div>
        </div>
      )}
      {isAccepted && !isOneTime && (
        <div className="proposal-banner proposal-banner-success">
          <CheckCircle size={18} />
          <div style={{ flex: 1 }}>
            <strong>Proposal accepted.</strong>{' '}
            {data.current_stage === 'contract'
              ? 'Continue to sign your Terms of Service.'
              : data.current_stage === 'onboarding'
              ? 'Continue to finish onboarding.'
              : 'Your account is set up.'}
          </div>
          <button
            type="button"
            className="proposal-action-btn proposal-action-primary"
            onClick={() => navigate(`/contract/${pathId}`)}
          >
            Continue
          </button>
        </div>
      )}
      {isDeclined && (
        <div className="proposal-banner proposal-banner-warn">
          <AlertCircle size={18} />
          <div>
            <strong>You declined this proposal.</strong>
            {data.lifecycle?.decline_reason && (
              <div className="proposal-decline-reason">Reason: {data.lifecycle.decline_reason}</div>
            )}
            <div>If you'd like to revisit, click Accept below.</div>
          </div>
        </div>
      )}

      {actionMessage && (
        <div
          className={`proposal-banner ${
            actionMessage.type === 'success' ? 'proposal-banner-success' : 'proposal-banner-error'
          }`}
        >
          {actionMessage.type === 'success' ? <CheckCircle size={18} /> : <XCircle size={18} />}
          <span>{actionMessage.text}</span>
        </div>
      )}

      <section className="proposal-body">
        {isCompliance
          ? <ComplianceProposalSummary data={data.proposal} compliance={data.compliance} />
          : data?.path_type === 'scoped_project'
            ? <ScopedProjectSummary data={data.proposal} />
            : isOneTime
              ? <ConstructionProposalSummary data={data.proposal} variant={isHardware ? 'hardware' : 'construction'} />
              : isInsurance
                ? <InsuranceProposalSummary data={data.proposal} />
                : <MspProposalSummary data={data.proposal} />}
      </section>

      {canAct && optionalAddons.length > 0 && !showDecline && (
        <section className="proposal-addons">
          <h3 style={{ marginTop: 24, marginBottom: 4 }}>Optional Add-Ons</h3>
          <p style={{ fontSize: 13, color: '#64748b', marginTop: 0 }}>
            Choose whether to include each add-on. Your selection is recorded
            with your acceptance and applied to your rate sheet.
          </p>
          {optionalAddons.map(a => {
            const choice = addonChoices[a.key];
            const yes = choice === true;
            const no = choice === false;
            return (
              <div
                key={a.key}
                style={{
                  border: '1px solid #e2e8f0', borderRadius: 8,
                  padding: 14, marginBottom: 10, background: '#fff',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, marginBottom: 2 }}>{a.label}</div>
                    <div style={{ fontSize: 13, color: '#475569' }}>{a.description}</div>
                  </div>
                  <div style={{ fontWeight: 600, color: '#0f172a', whiteSpace: 'nowrap', textAlign: 'right' }}>
                    {a.discountLabel && (
                      <div style={{ fontSize: 12, fontWeight: 400 }}>
                        <s style={{ color: '#94a3b8' }}>
                          ${a.monthlyFee.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </s>
                        {' '}<span style={{ color: '#16a34a' }}>{a.discountLabel}</span>
                      </div>
                    )}
                    ${(a.discountedFee ?? a.monthlyFee).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/mo
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                  <button
                    type="button"
                    onClick={() => setAddonChoices(p => ({ ...p, [a.key]: true }))}
                    style={{
                      padding: '6px 14px', fontSize: 13, fontWeight: 500,
                      borderRadius: 6, cursor: 'pointer',
                      border: `1px solid ${yes ? '#16a34a' : '#cbd5e1'}`,
                      background: yes ? '#16a34a' : 'white',
                      color: yes ? 'white' : '#475569',
                    }}
                  >
                    {yes && <CheckCircle size={14} style={{ verticalAlign: '-2px', marginRight: 4 }} />}
                    Add &mdash; ${a.monthlyFee.toFixed(2)}/mo
                  </button>
                  <button
                    type="button"
                    onClick={() => setAddonChoices(p => ({ ...p, [a.key]: false }))}
                    style={{
                      padding: '6px 14px', fontSize: 13, fontWeight: 500,
                      borderRadius: 6, cursor: 'pointer',
                      border: `1px solid ${no ? '#dc2626' : '#cbd5e1'}`,
                      background: no ? '#dc2626' : 'white',
                      color: no ? 'white' : '#475569',
                    }}
                  >
                    {no && <XCircle size={14} style={{ verticalAlign: '-2px', marginRight: 4 }} />}
                    Decline
                  </button>
                </div>
              </div>
            );
          })}
        </section>
      )}

      {canAct && (
        <section className="proposal-actions">
          {!showDecline ? (
            <div className="proposal-action-buttons">
              <button
                type="button"
                className="proposal-action-btn proposal-accept-btn"
                onClick={handleAccept}
                disabled={acting}
              >
                <CheckCircle size={16} /> Accept Proposal
              </button>
              <button
                type="button"
                className="proposal-action-btn proposal-decline-btn"
                onClick={() => setShowDecline(true)}
                disabled={acting}
              >
                <XCircle size={16} /> Decline
              </button>
              <button
                type="button"
                className="proposal-action-btn proposal-question-btn"
                onClick={() => commentThreadRef.current?.focus()}
                disabled={acting}
              >
                <MessageSquare size={16} /> Ask a Question
              </button>
            </div>
          ) : (
            <form className="proposal-decline-form" onSubmit={handleDeclineSubmit}>
              <label className="proposal-decline-label" htmlFor="decline-reason">
                Please share why you're declining (required):
              </label>
              <textarea
                id="decline-reason"
                rows={3}
                value={declineReason}
                onChange={e => setDeclineReason(e.target.value)}
                placeholder="e.g. Pricing is outside our budget, or we're going a different direction…"
                required
                disabled={acting}
              />
              <div className="proposal-decline-actions">
                <button
                  type="button"
                  className="proposal-action-btn proposal-cancel-btn"
                  onClick={() => { setShowDecline(false); setDeclineReason(''); }}
                  disabled={acting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="proposal-action-btn proposal-decline-btn"
                  disabled={acting || !declineReason.trim()}
                >
                  {acting ? <Loader2 className="spinner" size={16} /> : <XCircle size={16} />}
                  {' '}Submit Decline
                </button>
              </div>
            </form>
          )}
        </section>
      )}

      <ProposalCommentThread ref={commentThreadRef} pathId={pathId} />
    </div>
  );
}
